import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Auth0Provider } from './auth/Auth0Provider';
import { AuthGuard } from './auth/AuthGuard';
import { AuthProvider } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import ProtectedRoute from './components/ProtectedRoute';
import { storage } from './services/storage';

declare global {
  interface Window {
    __startTour?: () => void;
    __forceShowTour?: () => void;
  }
}
import Splash from './pages/Splash';
import { Auth0Callback } from './pages/Auth0Callback';
import NameCollectionWrapper from './pages/NameCollectionWrapper';
import Onboarding from './pages/Onboarding';
import LegalDisclaimer from './pages/LegalDisclaimer';
import Chat from './pages/Chat';
import MyPath from './pages/MyPath';
import Insights from './pages/Insights';
import { ResourceDetail } from './pages/ResourceDetail';
import Settings from './pages/Settings';
import GoalDetail from './pages/GoalDetail';
import PlanDetail from './pages/PlanDetail'; // 🔧 FIX JAN 21: Add missing PlanDetail import
import WorkoutDetail from './pages/WorkoutDetail'; // 🔧 FIX JAN 23: Add missing WorkoutDetail import
import { MobilityDetail } from './pages/MobilityDetail'; // 🔧 FIX JAN 23: Add missing MobilityDetail import (named export)
import RestDayDetail from './pages/RestDayDetail'; // 🆕 JAN 21, 2026: Rest day detail page
import PlansReview from './pages/PlansReview';
import { AuthEntryPreview } from './pages/AuthEntryPreview';
import SplashPreview from './pages/SplashPreview';
// Removed global BottomNav - all pages now use Anima navigation components
import { OnboardingTour } from './components/OnboardingTour';
import FeedbackIntelligenceDemo from './components/FeedbackIntelligenceDemo';
import { HealthLabsDemo } from './components/HealthLabsDemo';
import SliderDiagnostic from './components/SliderDiagnostic';
import { PersistentNavigation } from './components/PersistentNavigation';
import { ChatResponseNotification } from './components/notifications/ChatResponseNotification';
import { InAppNotificationBannerManager } from './components/notifications/InAppNotificationBanner';
import { useGlobalNotificationPolling } from './hooks/useGlobalNotificationPolling';

// ADMIN IMPORTS REMOVED - Now in separate AdminApp.tsx
import { setUserStorage, getCurrentUserId } from './utils/userStorage';
// InviteLoginPage removed - using original query parameter design

// Admin App - COMPLETELY ISOLATED
import AdminApp from './AdminApp';

// Platform Analytics — capture UTMs from the landing URL before React Router
// takes over. Must run at module-eval time (not in a useEffect) so the query
// string is still intact. No-op on native.
import { captureAttributionFromLandingURL } from './utils/attributionCapture';
void captureAttributionFromLandingURL();

// Page transition component - SIMPLIFIED to remove flickering
const PageTransition: React.FC<{ 
  children: React.ReactNode;
  location: { pathname: string };
}> = ({ children, location }) => {
  // Simplified - no complex transitions that cause flickering
  return (
    <div className="page-transition-container">
      <div className="page-transition-content" key={location.pathname}>
        {children}
      </div>
    </div>
  );
};

// Layout component that includes the PersistentNavigation and global tour
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [showTour, setShowTour] = useState(false);

  // 🔔 Global notification polling — fires banners on ALL pages (not just chat)
  useGlobalNotificationPolling();

  // 🔔 Chat Response Notification State
  const [chatNotification, setChatNotification] = useState<{
    message: string;
    responsePreview: string;
  } | null>(null);

  // Global tour functions
  useEffect(() => {
    window.__startTour = () => setShowTour(true);
    window.__forceShowTour = () => {
      setShowTour(true);
    };

    // Listen for tour start event from Chat component
    const handleStartTour = async () => {
      console.log('🎯 App.tsx received startOnboardingTour event');
      setShowTour(true);
      
      // 🚀 PRE-WARM: Trigger background generation of Step 1 response during tour
      // This makes "What's your name?" appear instantly when tour ends
      console.log('🚀 PRE-WARM: Triggering background generation of Step 1 response');
      try {
        const jwtToken = await storage.getItem('jwt_token');
        if (jwtToken) {
          // Non-blocking background call - don't await
          fetch('/api/chat/prewarm-step1', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${jwtToken}`
            }
          }).then(response => {
            if (response.ok) {
              console.log('✅ PRE-WARM: Step 1 response generated during tour');
            } else {
              console.warn('⚠️ PRE-WARM: Failed to generate Step 1 response (non-critical)');
            }
          }).catch(error => {
            console.warn('⚠️ PRE-WARM: Error during pre-generation (non-critical):', error);
          });
        }
      } catch (error) {
        // Non-critical - tour continues normally
        console.warn('⚠️ PRE-WARM: Pre-generation failed (non-critical):', error);
      }
    };

    window.addEventListener('startOnboardingTour', handleStartTour);
    
    return () => {
      window.removeEventListener('startOnboardingTour', handleStartTour);
    };
  }, []);

  // 🔔 Listen for chat response notifications from Mission 11 infrastructure
  useEffect(() => {
    const handleChatResponseReady = (event: CustomEvent) => {
      const { message, responsePreview } = event.detail;
      
      // Only show notification if user is NOT on chat page
      if (location.pathname !== '/chat') {
        console.log('🔔 NOTIFICATION: Chat response ready, showing in-app notification');
        setChatNotification({
          message: message || 'Your response is ready!',
          responsePreview: responsePreview || 'Tap to view Cedar\'s response'
        });
        
        // Auto-dismiss after 8 seconds
        setTimeout(() => {
          setChatNotification(null);
        }, 8000);
      }
    };

    // Listen for custom event from notification system
    window.addEventListener('chatResponseReady', handleChatResponseReady as EventListener);
    
    return () => {
      window.removeEventListener('chatResponseReady', handleChatResponseReady as EventListener);
    };
  }, [location.pathname]);

  const handleTourComplete = () => {
    setShowTour(false);
    const userId = getCurrentUserId();
    // Use consistent storage key with Chat.tsx
    setUserStorage('onboarding_tour_seen', 'true', userId);
    console.log('🎯 Tour completed, setting onboarding_tour_seen for user:', userId);
  };

  // Determine if we should show navigation - show on main app pages, hide on splash/onboarding/legal
  const shouldShowNavigation = location.pathname === '/chat' ||
                               location.pathname === '/mypath' ||
                               location.pathname === '/insights' ||
                               location.pathname === '/settings' ||
                               location.pathname.startsWith('/goal/') ||
                               location.pathname.startsWith('/plan/') ||
                               location.pathname.startsWith('/workout/') ||
                               location.pathname.startsWith('/mobility/') ||
                               location.pathname.startsWith('/rest-day/') ||
                               location.pathname.startsWith('/plans-review');
                               // HEALTH SECTION TEMPORARILY REMOVED FOR MVP
                               // location.pathname === '/health' ||
                               // location.pathname.startsWith('/health/');

  // Background styling
  const useGreyBackground = !location.pathname.startsWith('/chat');
  
  // Main app is always mobile-constrained (admin routes are in separate AdminApp)

  return (
    <div className={`min-h-screen ${useGreyBackground ? 'bg-gray-50' : 'bg-white'}`}>
      <div className="w-full max-w-[390px] mx-auto">
        <main>
          <PageTransition location={location}>
            {children}
          </PageTransition>
        </main>
        
        {showTour && (
          <OnboardingTour 
            onComplete={handleTourComplete}
          />
        )}
        
        {/* 🔔 Chat Response Notification - shows on all screens except chat */}
        {chatNotification && (
          <ChatResponseNotification
            message={chatNotification.message}
            responsePreview={chatNotification.responsePreview}
            onDismiss={() => setChatNotification(null)}
            onNavigateToChat={() => setChatNotification(null)}
          />
        )}
        
        {/* 📥 In-App Notification Banner Manager - Global listener for all notification events */}
        <InAppNotificationBannerManager />
      </div>
      
      {/* Global Persistent Navigation - OUTSIDE scrollable container for true fixed positioning */}
      {shouldShowNavigation && (
        <PersistentNavigation />
      )}
    </div>
  );
};

function App() {
  // BASIC LOGGING TO VERIFY APP LOADS
  const APP_VERSION = '2026.01.23.001'; // Force rebuild for bundle sync
  console.log('🚨 APP COMPONENT LOADED! Version:', APP_VERSION);
  console.log('🚨 Current location:', window.location.href);
  
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  const isFirebaseHosting = hostname.includes('coach-cedar-beta.web.app') || hostname.includes('coachcedar-com.web.app');
  const isMainDomain = hostname === 'coachcedar.com' || hostname === 'www.coachcedar.com';
  const isInvitePath = pathname.startsWith('/invite');
  const isAdminPath = pathname.startsWith('/admin');

  const shouldUseLandingRouting = ((isFirebaseHosting || isMainDomain) && !isInvitePath) || isAdminPath;

  // DEBUG ROUTING LOGIC
  console.log('🚨 ROUTING DEBUG:', {
    hostname,
    pathname,
    isFirebaseHosting,
    isMainDomain,
    isInvitePath,
    isAdminPath,
    shouldUseLandingRouting
  });

  if (shouldUseLandingRouting) {
    // Use completely isolated AdminApp - NO admin code in main app bundle
    return <AdminApp />;
  }

  // App routes (existing Coach Cedar app) - for app.coachcedar.com and localhost (non-admin paths)
  return (
    <SafeAreaProvider>
    <Auth0Provider>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Local-only: preview the branded Auth entry screen on localhost */}
            {typeof window !== 'undefined' &&
              (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                <>
                  <Route path="/__dev/auth" element={<AuthEntryPreview />} />
                  <Route path="/__dev/splash" element={<SplashPreview />} />
                </>
              )}

            {/* Auth0 callback route - OUTSIDE AuthGuard to process callbacks */}
            <Route path="/auth/callback" element={<Auth0Callback />} />
            
            {/* All other routes protected by AuthGuard */}
            <Route path="/*" element={
              <AuthGuard>
                <ProfileProvider>
                  <Routes>
                    {/* Invite tokens handled via query parameters in Splash component */}
                    
                    {/* Main app routes - wrapped in Layout */}
                    <Route path="/*" element={
                      <Layout>
                        <Routes>
                          <Route path="/" element={<Splash />} />
                          <Route path="/collect-name" element={
                            <ProtectedRoute>
                              <NameCollectionWrapper />
                            </ProtectedRoute>
                          } />
                  <Route path="/onboarding" element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  } />
                  <Route path="/legal" element={
                    <ProtectedRoute>
                      <LegalDisclaimer />
                    </ProtectedRoute>
                  } />
                  <Route path="/chat" element={
                    <ProtectedRoute>
                      <Chat />
                    </ProtectedRoute>
                  } />
                  <Route path="/mypath" element={
                    <ProtectedRoute>
                      <MyPath />
                    </ProtectedRoute>
                  } />
                  <Route path="/goal/:id" element={
                    <ProtectedRoute>
                      <GoalDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/plan/:id" element={
                    <ProtectedRoute>
                      <PlanDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/workout/:id" element={
                    <ProtectedRoute>
                      <WorkoutDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/mobility/:id" element={
                    <ProtectedRoute>
                      <MobilityDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/rest-day/:id" element={
                    <ProtectedRoute>
                      <RestDayDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/plans-review" element={
                    <ProtectedRoute>
                      <PlansReview />
                    </ProtectedRoute>
                  } />
                  {/* HEALTH SECTION TEMPORARILY REMOVED FOR MVP
                  <Route path="/health" element={
                    <ProtectedRoute>
                      <Health />
                    </ProtectedRoute>
                  } />
                  <Route path="/health/sleep/:date" element={
                    <ProtectedRoute>
                      <SleepDetail />
                    </ProtectedRoute>
                  } />
                  */}
                  <Route path="/insights" element={
                    <ProtectedRoute>
                      <Insights />
                    </ProtectedRoute>
                  } />
                  <Route path="/insights/resource/:resourceId" element={
                    <ProtectedRoute>
                      <ResourceDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  } />
                  <Route path="/feedback-demo" element={
                    <ProtectedRoute>
                      <FeedbackIntelligenceDemo />
                    </ProtectedRoute>
                  } />
                  <Route path="/health-labs-demo" element={
                    <ProtectedRoute>
                      <HealthLabsDemo />
                    </ProtectedRoute>
                  } />
                  <Route path="/slider-diagnostic" element={
                    <ProtectedRoute>
                      <SliderDiagnostic />
                    </ProtectedRoute>
                  } />
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                      </Layout>
                    } />
                  </Routes>
                </ProfileProvider>
              </AuthGuard>
            } />
          </Routes>
        </Router>
      </AuthProvider>
    </Auth0Provider>
    </SafeAreaProvider>
  );
}

export default App;
// Force frontend deployment - Nov 19, 2025
